/**
 * Product Information Audit Service
 * 
 * Frontend service for interacting with product information audit APIs.
 */

import { apiClient } from './api.client';
import {
  AuditTrailQuery,
  AuditTrailResponse,
  ProductInfoVersion,
  ProductInfoComparison,
  RollbackRequest,
  RollbackResponse
} from '../types/enhanced-product-info.types';

class ProductInfoAuditService {
  
  /**
   * Gets audit trail for a specific batch or system-wide
   */
  async getAuditTrail(query: AuditTrailQuery): Promise<AuditTrailResponse> {
    try {
      if (query.batchId) {
        // Batch-specific audit trail
        const response = await apiClient.get(`/product-info-audit/trail/${query.batchId}`, {
          params: {
            changeType: query.changeType,
            startDate: query.startDate?.toISOString(),
            endDate: query.endDate?.toISOString(),
            page: query.page?.toString(),
            limit: query.limit?.toString()
          }
        });
        return response.data.data;
      } else {
        // System-wide search
        const response = await apiClient.get('/product-info-audit/search', {
          params: {
            changeType: query.changeType,
            changedBy: query.changedBy,
            startDate: query.startDate?.toISOString(),
            endDate: query.endDate?.toISOString(),
            page: query.page?.toString(),
            limit: query.limit?.toString()
          }
        });
        return response.data.data;
      }
    } catch (error) {
      console.error('Failed to get audit trail:', error);
      throw new Error('Failed to retrieve audit trail');
    }
  }

  /**
   * Gets version history for a specific batch
   */
  async getVersionHistory(batchId: string): Promise<ProductInfoVersion[]> {
    try {
      const response = await apiClient.get(`/product-info-audit/versions/${batchId}`);
      return response.data.data.versions;
    } catch (error) {
      console.error('Failed to get version history:', error);
      throw new Error('Failed to retrieve version history');
    }
  }

  /**
   * Compares two versions of product information
   */
  async compareVersions(
    batchId: string,
    version1: number,
    version2: number
  ): Promise<ProductInfoComparison> {
    try {
      const response = await apiClient.get(
        `/product-info-audit/compare/${batchId}/${version1}/${version2}`
      );
      return response.data.data;
    } catch (error) {
      console.error('Failed to compare versions:', error);
      throw new Error('Failed to compare versions');
    }
  }

  /**
   * Rolls back product information to a previous version
   */
  async rollbackToVersion(request: RollbackRequest): Promise<RollbackResponse> {
    try {
      const response = await apiClient.post('/product-info-audit/rollback', request);
      return response.data.data;
    } catch (error) {
      console.error('Failed to rollback version:', error);
      throw new Error('Rollback failed');
    }
  }

  /**
   * Gets audit summary for a specific batch
   */
  async getAuditSummary(batchId: string): Promise<any> {
    try {
      const response = await apiClient.get(`/product-info-audit/summary/${batchId}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to get audit summary:', error);
      throw new Error('Failed to retrieve audit summary');
    }
  }

  /**
   * Gets system-wide audit statistics
   */
  async getAuditStats(timeframe: string = '30d'): Promise<any> {
    try {
      const response = await apiClient.get('/product-info-audit/stats', {
        params: { timeframe }
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to get audit statistics:', error);
      throw new Error('Failed to retrieve audit statistics');
    }
  }

  /**
   * Searches audit trail across multiple batches
   */
  async searchAuditTrail(query: Omit<AuditTrailQuery, 'batchId'>): Promise<AuditTrailResponse> {
    try {
      const response = await apiClient.get('/product-info-audit/search', {
        params: {
          changeType: query.changeType,
          changedBy: query.changedBy,
          startDate: query.startDate?.toISOString(),
          endDate: query.endDate?.toISOString(),
          page: query.page?.toString(),
          limit: query.limit?.toString()
        }
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to search audit trail:', error);
      throw new Error('Failed to search audit trail');
    }
  }

  /**
   * Exports audit trail data
   */
  async exportAuditTrail(query: AuditTrailQuery, format: 'csv' | 'json' = 'csv'): Promise<Blob> {
    try {
      const response = await apiClient.get('/product-info-audit/export', {
        params: {
          ...query,
          format,
          startDate: query.startDate?.toISOString(),
          endDate: query.endDate?.toISOString()
        },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Failed to export audit trail:', error);
      throw new Error('Failed to export audit trail');
    }
  }

  /**
   * Gets audit trail for multiple batches
   */
  async getBatchesAuditTrail(batchIds: string[]): Promise<Record<string, AuditTrailResponse>> {
    try {
      const promises = batchIds.map(async (batchId) => {
        const auditTrail = await this.getAuditTrail({ batchId, limit: 50 });
        return { batchId, auditTrail };
      });

      const results = await Promise.all(promises);
      
      return results.reduce((acc, { batchId, auditTrail }) => {
        acc[batchId] = auditTrail;
        return acc;
      }, {} as Record<string, AuditTrailResponse>);

    } catch (error) {
      console.error('Failed to get batches audit trail:', error);
      throw new Error('Failed to retrieve batches audit trail');
    }
  }

  /**
   * Gets recent activity across all batches
   */
  async getRecentActivity(limit: number = 20): Promise<any[]> {
    try {
      const response = await this.getAuditTrail({ 
        page: 1, 
        limit,
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) // Last 7 days
      });
      
      return response.auditTrail;
    } catch (error) {
      console.error('Failed to get recent activity:', error);
      throw new Error('Failed to retrieve recent activity');
    }
  }
}

// Export singleton instance
export const productInfoAuditService = new ProductInfoAuditService();